import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Sun, 
  MessageCircle, 
  Heart, 
  Shield,
  Star,
  Moon,
  Calendar,
  User,
  ChevronRight,
  Loader2,
  Bot
} from 'lucide-react';
import axios from 'axios';
import VedVaaniChat from '../components/VedVaaniChat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Zodiac signs data
const RASHIS = [
  { name: 'Mesh', nameHi: 'मेष', english: 'Aries', icon: '♈', color: '#FF6B6B' },
  { name: 'Vrishabh', nameHi: 'वृषभ', english: 'Taurus', icon: '♉', color: '#4ECDC4' },
  { name: 'Mithun', nameHi: 'मिथुन', english: 'Gemini', icon: '♊', color: '#FFE66D' },
  { name: 'Kark', nameHi: 'कर्क', english: 'Cancer', icon: '♋', color: '#95E1D3' },
  { name: 'Singh', nameHi: 'सिंह', english: 'Leo', icon: '♌', color: '#F38181' },
  { name: 'Kanya', nameHi: 'कन्या', english: 'Virgo', icon: '♍', color: '#AA96DA' },
  { name: 'Tula', nameHi: 'तुला', english: 'Libra', icon: '♎', color: '#FCBAD3' },
  { name: 'Vrishchik', nameHi: 'वृश्चिक', english: 'Scorpio', icon: '♏', color: '#A8D8EA' },
  { name: 'Dhanu', nameHi: 'धनु', english: 'Sagittarius', icon: '♐', color: '#FF9F43' },
  { name: 'Makar', nameHi: 'मकर', english: 'Capricorn', icon: '♑', color: '#6C5CE7' },
  { name: 'Kumbh', nameHi: 'कुंभ', english: 'Aquarius', icon: '♒', color: '#00CEC9' },
  { name: 'Meen', nameHi: 'मीन', english: 'Pisces', icon: '♓', color: '#FD79A8' }
];

export default function AstrologyPage({ user }) {
  const navigate = useNavigate();
  const [selectedRashi, setSelectedRashi] = useState(null);
  const [dailyRashifal, setDailyRashifal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topAstrologers, setTopAstrologers] = useState([]);
  const [showVedVaani, setShowVedVaani] = useState(false);

  useEffect(() => {
    fetchTopAstrologers();
  }, []);

  const fetchTopAstrologers = async () => {
    try {
      const response = await axios.get(`${API}/astrologers/top`);
      setTopAstrologers(response.data);
    } catch (error) {
      // Use demo data if API not available
      setTopAstrologers([
        { id: '1', name: 'Pt. Ramesh Sharma', specialization: 'Vedic Astrology', rating: 4.9, price_per_min: 15, experience: 20, avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=RS' },
        { id: '2', name: 'Acharya Priya Devi', specialization: 'Kundali Matching', rating: 4.8, price_per_min: 12, experience: 15, avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=PD' },
        { id: '3', name: 'Jyotishi Suresh Kumar', specialization: 'Remedies & Doshas', rating: 4.7, price_per_min: 10, experience: 12, avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=SK' },
      ]);
    }
  };

  const services = [
    {
      id: 'kundali',
      title: 'Generate Kundali',
      titleHi: 'कुंडली बनाएं',
      description: 'Create your birth chart with detailed planetary positions',
      icon: Sparkles,
      color: 'from-purple-500 to-indigo-600',
      path: '/jyotish/kundali'
    },
    {
      id: 'rashifal',
      title: 'Daily Rashifal',
      titleHi: 'दैनिक राशिफल',
      description: 'Get your daily horoscope predictions',
      icon: Sun,
      color: 'from-orange-500 to-amber-500',
      path: '/jyotish/rashifal'
    },
    {
      id: 'astrologer',
      title: 'Talk to Astrologer',
      titleHi: 'ज्योतिषी से बात करें',
      description: 'Consult expert astrologers online',
      icon: MessageCircle,
      color: 'from-green-500 to-emerald-600',
      path: '/jyotish/astrologers'
    },
    {
      id: 'matching',
      title: 'Kundali Matching',
      titleHi: 'कुंडली मिलान',
      description: 'Check compatibility for marriage',
      icon: Heart,
      color: 'from-pink-500 to-rose-600',
      path: '/jyotish/matching'
    },
    {
      id: 'remedies',
      title: 'Remedies',
      titleHi: 'उपाय',
      description: 'Astrological remedies for doshas',
      icon: Shield,
      color: 'from-teal-500 to-cyan-600',
      path: '/jyotish/remedies'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Moon className="w-8 h-8 text-yellow-300" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
              धर्मज्योतिष
            </h1>
            <Star className="w-8 h-8 text-yellow-300" />
          </div>
          <p className="text-purple-200 text-lg">DharmJyotish - Vedic Astrology</p>
          <p className="text-purple-300 text-sm mt-2" style={{ fontFamily: 'Noto Sans Devanagari, serif' }}>
            ज्योतिषं सर्वशास्त्राणां चक्षुः परमं स्मृतम्
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-8">
        {/* Services Grid */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Our Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => navigate(service.path)}
                className="group relative bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left overflow-hidden"
                data-testid={`service-${service.id}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-3`}>
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm">{service.title}</h3>
                <p className="text-xs text-purple-600 font-medium" style={{ fontFamily: 'Noto Sans Devanagari, serif' }}>
                  {service.titleHi}
                </p>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{service.description}</p>
                <ChevronRight className="absolute bottom-4 right-4 w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
              </button>
            ))}
          </div>
        </section>

        {/* VedVaani AI Jyotish Section */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Bot className="w-5 h-5 text-amber-500" />
            AI Jyotish Guide
          </h2>
          <button
            onClick={() => setShowVedVaani(true)}
            className="w-full bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900 rounded-2xl p-6 text-left hover:shadow-2xl transition-all duration-300 group relative overflow-hidden"
            data-testid="vedvaani-card"
          >
            {/* Animated stars background */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute bg-white rounded-full animate-pulse"
                  style={{
                    width: Math.random() * 2 + 1 + 'px',
                    height: Math.random() * 2 + 1 + 'px',
                    top: Math.random() * 100 + '%',
                    left: Math.random() * 100 + '%',
                    animationDelay: Math.random() * 3 + 's',
                    opacity: 0.3
                  }}
                />
              ))}
            </div>
            
            <div className="relative flex items-start gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-amber-300" style={{ fontFamily: 'Merriweather, serif' }}>
                    Ask VedVaani AI
                  </h3>
                  <span className="bg-green-500/20 text-green-400 text-[10px] px-2 py-0.5 rounded-full border border-green-500/30">
                    FREE
                  </span>
                </div>
                <p className="text-sm text-amber-200/70" style={{ fontFamily: 'Noto Sans Devanagari, serif' }}>
                  वेदवाणी - AI ज्योतिष मार्गदर्शक
                </p>
                <p className="text-sm text-purple-300 mt-2">
                  Get instant Vedic astrology guidance on Kundali, Rashis, Doshas, and spiritual remedies
                </p>
                <div className="flex items-center gap-4 mt-3 text-xs text-purple-400">
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-400" />
                    24/7 Available
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3 h-3 text-amber-400" />
                    Instant Response
                  </span>
                  <span className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-amber-400" />
                    Private & Secure
                  </span>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-amber-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </section>

        {/* Daily Rashifal Quick Access */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Sun className="w-5 h-5 text-orange-500" />
            Today's Rashifal
            <span className="text-sm font-normal text-gray-500">
              {new Date().toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </h2>
          <div className="bg-white rounded-2xl p-4 shadow-md">
            <p className="text-sm text-gray-600 mb-4">Select your Rashi to view today's predictions</p>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
              {RASHIS.map((rashi) => (
                <button
                  key={rashi.name}
                  onClick={() => {
                    setSelectedRashi(rashi);
                    navigate(`/jyotish/rashifal/${rashi.name.toLowerCase()}`);
                  }}
                  className="flex flex-col items-center p-3 rounded-xl hover:bg-purple-50 transition-colors group"
                  data-testid={`rashi-${rashi.name.toLowerCase()}`}
                >
                  <span 
                    className="text-2xl mb-1 group-hover:scale-110 transition-transform"
                    style={{ color: rashi.color }}
                  >
                    {rashi.icon}
                  </span>
                  <span className="text-xs font-medium text-gray-700">{rashi.nameHi}</span>
                  <span className="text-[10px] text-gray-500">{rashi.english}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Top Astrologers */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Top Astrologers
            </h2>
            <button 
              onClick={() => navigate('/jyotish/astrologers')}
              className="text-sm text-purple-600 font-medium hover:text-purple-700"
            >
              View All →
            </button>
          </div>
          <div className="space-y-3">
            {topAstrologers.map((astrologer) => (
              <div
                key={astrologer.id}
                className="bg-white rounded-xl p-4 shadow-md flex items-center gap-4 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/jyotish/astrologer/${astrologer.id}`)}
                data-testid={`astrologer-${astrologer.id}`}
              >
                <img
                  src={astrologer.avatar}
                  alt={astrologer.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-purple-200"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800">{astrologer.name}</h3>
                  <p className="text-sm text-gray-500">{astrologer.specialization}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-500">{astrologer.experience} yrs exp</span>
                    <span className="flex items-center gap-1 text-xs">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      {astrologer.rating}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">₹{astrologer.price_per_min}</p>
                  <p className="text-xs text-gray-500">/min</p>
                  <button className="mt-2 px-4 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-medium rounded-full hover:shadow-md transition-shadow">
                    Chat
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/jyotish/kundali')}
            className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-2xl p-5 text-left hover:shadow-xl transition-shadow"
            data-testid="quick-kundali"
          >
            <Sparkles className="w-8 h-8 mb-3" />
            <h3 className="font-bold text-lg">Free Kundali</h3>
            <p className="text-purple-200 text-sm">Generate your birth chart now</p>
          </button>
          <button
            onClick={() => navigate('/jyotish/matching')}
            className="bg-gradient-to-br from-pink-500 to-rose-600 text-white rounded-2xl p-5 text-left hover:shadow-xl transition-shadow"
            data-testid="quick-matching"
          >
            <Heart className="w-8 h-8 mb-3" />
            <h3 className="font-bold text-lg">Kundali Match</h3>
            <p className="text-pink-200 text-sm">Check marriage compatibility</p>
          </button>
        </section>

        {/* Become an Astrologer CTA */}
        {user && user.role !== 'astrologer' && (
          <section className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-6 border border-amber-200">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                <Star className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 text-lg">Become an Astrologer</h3>
                <p className="text-gray-600 text-sm">Share your knowledge and earn on DharmJyotish</p>
              </div>
              <button
                onClick={() => navigate('/jyotish/register-astrologer')}
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium rounded-full hover:shadow-lg transition-shadow"
                data-testid="become-astrologer-btn"
              >
                Apply Now
              </button>
            </div>
          </section>
        )}
      </div>

      {/* VedVaani Chat Interface */}
      <VedVaaniChat 
        isOpen={showVedVaani} 
        onClose={() => setShowVedVaani(false)} 
        user={user}
      />

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
